import { useState, useEffect, useRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import ChatMessage from "./components/ChatMessage";
import ChatInput from "./components/ChatInput";
import EmailModal from "./components/EmailModal";
import { useSearchParams } from "react-router-dom";
const API = `${process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000"}/query`;

export default function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const recaptchaRef = useRef();
  const querySentRef = useRef(false);
  const [userEmail, setUserEmail] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let savedEmail = localStorage.getItem("chat_user_email");
    if (savedEmail) setUserEmail(savedEmail);
  }, []);

  useEffect(() => {
    let id = localStorage.getItem("chat_session_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("chat_session_id", id);
    }
    setSessionId(id);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const handleEmailSubmit = (email) => {
    localStorage.setItem("chat_user_email", email);
    setUserEmail(email);
  };

  const sendMessage = useCallback(async (directMessage) => {
    const question = (directMessage ?? input).trim();
    if (!question || !sessionId) return;

    const captchaToken = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();

    setMessages((p) => [...p, { from: "user", content: question }]);
    setInput("");
    setIsTyping(true);

    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          session_id: sessionId,
          username: "web_user",
          email: userEmail,
          captchaToken,
        }),
      });

      const data = await res.json();
      console.log("data", data);
      setMessages((p) => [...p, { from: "bot", content: data }]);
    } catch {
      setMessages((p) => [
        ...p,
        { from: "bot", content: { success: false, message: "Network error" } },
      ]);
    } finally {
      setIsTyping(false);
    }
  }, [sessionId, userEmail, input]);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q && sessionId && userEmail && !querySentRef.current) {
      querySentRef.current = true;
      sendMessage(`Give me all information of ${q}`);
    }
  }, [sessionId, userEmail, searchParams, sendMessage]);

  const hasConversationStarted = messages.length > 0;

  return (
    <div className="flex h-screen bg-white text-black flex-col">
      {!userEmail && <EmailModal onSubmit={handleEmailSubmit} />}
      <ReCAPTCHA
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        size="invisible"
        ref={recaptchaRef}
      />

      {!hasConversationStarted ? (
        <div className="flex h-screen bg-white text-black">
          <div className="m-auto text-center max-w-2xl px-6">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Patent Knowledge Agent
            </h1>
            <p className="text-lg text-gray-700">
              Ask me questions about IPR trials And Re-exams —
              patent numbers, counsel details, Re-exams status and more.
            </p>
            <p>Just type your question below and press Enter</p>
          </div>
          <ChatInput
            input={input}
            setInput={setInput}
            sendMessage={sendMessage}
            isTyping={isTyping}
            welcome
          />
        </div>
      ) : (
        <>
          <header className="p-4 border-b border-[#cccccc] bg-white sticky top-0 z-10">
            <h1 className="text-lg font-semibold text-center">
              Patent Knowledge Agent BETA 3.1 Testing Phase
            </h1>
          </header>

          <main className="flex-1 overflow-y-auto pb-44 bg-white">
            <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
              {messages.map((m, i) => (
                <ChatMessage key={i} message={m} />
              ))}

              {isTyping && (
                <div className="flex gap-3 items-center text-gray-400 text-sm pl-14">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce delay-150"></div>
                    <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce delay-300"></div>
                  </div>
                  <span>Thinking...</span>
                </div>
              )}

              <div ref={scrollRef} />
            </div>
          </main>

          <ChatInput
            input={input}
            setInput={setInput}
            sendMessage={sendMessage}
            isTyping={isTyping}
          />
        </>
      )}
    </div>
  );
}
